import { Component } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import * as addDays from 'date-fns/add_days';
import * as addMonths from 'date-fns/add_months';
import * as addWeeks from 'date-fns/add_weeks';
import * as addQuarters from 'date-fns/add_quarters';
import * as addYears from 'date-fns/add_years';
import * as isBefore from 'date-fns/is_before';
import * as distanceInWordsStrict from 'date-fns/distance_in_words_strict';

import { Frequency, RecurringActions } from '../transaction-store/recurring';
import { AppSettings } from '../app.settings';
import { NgbDateNativeAdapter } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { Donation } from '../transaction-store/donation';
import {
  differenceInMonths,
  differenceInQuarters,
  differenceInYears,
  differenceInWeeks
} from 'date-fns';
import { GaClient } from '../../init';
import { Sdk } from '../core/sdk.service';
import { isApplePayRecurringFrequency } from '../shared/apple-pay-recurring';

const threeMonthsOut = addMonths(new Date(), 3);

@Component({
  selector: 'app-recurring',
  template: `
    <section class="container" *ngIf="!isHidden">
      <div class="row">
        <select-field
          class="col-12"
          label="How often will you donate?"
          [items]="frequencies"
          [ngModel]="selected.val"
          (ngModelChange)="onChange($event); calcEndDate($event, count)"
        >
        </select-field>
      </div>
    </section>
    <section
      class="container"
      *ngIf="
        !isHidden &&
        selected.val != Frequency.Once &&
        (showStartDate || recurringMethod)
      "
    >
      <div class="row">
        <date-field
          type="date"
          class="col-12 col-sm"
          label="Start Date"
          [(ngModel)]="start"
          (blur)="setStartDate($event)"
          (update)="setStartDate($event)"
          *ngIf="showStartDate"
          [frequency]="selected.val"
          [minDate]="minDate"
          [maxDate]="startLimit"
          [required]="true"
        >
        </date-field>
        <date-field
          type="date"
          class="col-12 col-sm"
          label="End Date"
          [(ngModel)]="end"
          [frequency]="selected.val"
          (update)="setEndDate($event)"
          (blur)="setEndDate($event)"
          *ngIf="recurringMethod == 'date'"
          [minDate]="minEndDate || minDate"
          [required]="!!endDate"
        >
        </date-field>
        <number-field
          class="col-12 col-sm"
          type="number"
          label="Number of Donations"
          [(ngModel)]="count"
          (ngModelChange)="calcEndDate(selected.val, $event)"
          [required]="true"
          min="1"
          *ngIf="recurringMethod == 'count'"
        >
        </number-field>
      </div>
      <div
        class="row"
        *ngIf="indefiniteEndMessage && !(count || endDate)"
        class="recurring-text"
      >
        <div class="form-group small-text">
          <p class="primary-color">{{ indefiniteEndMessage }}</p>
        </div>
      </div>
      <div class="row" *ngIf="clarification" class="recurring-text">
        <div class="form-group small-text">
          You will be charged
          {{ clarification.amount | currency: currencyCode:'symbol-narrow' }}
          for a total of
          {{ clarification.total | currency: currencyCode:'symbol-narrow' }}
          over {{ clarification.length }}
        </div>
      </div>
    </section>
  `,
  providers: [NgbDateNativeAdapter]
})
export class RecurringComponent {
  selected = { text: 'Once', val: Frequency.Once };
  Frequency = Frequency;
  recurringMethod;
  showStartDate = false;
  count;
  clarification;
  startDate;
  endDate;
  indefiniteEndMessage;
  start;
  end;
  currencyCode = 'USD';
  startLimit = this.dateAdapter.fromModel(threeMonthsOut);
  minDate = this.dateAdapter.fromModel(new Date());
  minEndDate = this.minDate;
  amount = 0;
  isHidden = false;

  @Select() donation$: Observable<Donation>;
  frequencies = [
    { text: 'Once', val: Frequency.Once },
    { text: 'Every 2 Weeks', val: Frequency.BiWeekly },
    { text: '1st and 15th', val: Frequency.TwiceMonthly },
    { text: 'Weekly', val: Frequency.Weekly },
    { text: 'Monthly', val: Frequency.Monthly },
    { text: 'Quarterly', val: Frequency.Quarterly },
    { text: 'Semi-Annually', val: Frequency.SemiAnnually },
    { text: 'Annually', val: Frequency.Annually }
  ];
  // the embed's full frequency list; `frequencies` is what the picker shows
  private allFrequencies = this.frequencies;
  private paymentType: string;
  // flips once idonatelib confirms the device can issue a merchant token
  private applePayRecurringSupported = false;

  constructor(
    private store: Store,
    private settings: AppSettings,
    private dateAdapter: NgbDateNativeAdapter,
    private sdk: Sdk
  ) {
    this.currencyCode = this.settings.getValue('organization').currency_code;
    const properties = this.settings.getValue('properties');
    const embedCashFrequencies = properties.cash_frequencies;
    const frequencyOptions = properties.frequency_options;
    this.recurringMethod = properties.recurring_method;
    // regardless of the recurring type, we should initialize start date to 0:00:00
    // we also need to dispatch to the store to prevent the default of Date() being used
    // leaving just Date() in play causes unintentional future date donations for late night donors.
    if (properties.start_date) {
      this.startDate = new Date(properties.start_date);
    } else {
      this.startDate = new Date();
    }
    this.startDate = new Date(
      this.startDate.getFullYear(),
      this.startDate.getMonth(),
      this.startDate.getDate(),
      0,
      0,
      0,
      0
    );
    this.store.dispatch(new RecurringActions.SetStartDate(this.startDate));
    if (this.recurringMethod === 'date' || this.recurringMethod === 'count') {
      this.indefiniteEndMessage = properties.indefinite_end_message;
    }

    this.showStartDate = Boolean(+properties.show_start_date);
    if (this.showStartDate) {
      this.start = {
        year: this.startDate.getFullYear(),
        month: this.startDate.getMonth() + 1,
        day: this.startDate.getDate()
      };
    }
    this.setClarification();
    this.frequencies = frequencyOptions.map(item => ({
      val: item,
      text: embedCashFrequencies[item]
    }));

    this.allFrequencies = this.frequencies;
    this.selected = this.frequencies[0];
    this.sdk.isApplePayRecurringSupported().then(supported => {
      this.applePayRecurringSupported = supported;
      this.applyPaymentType();
    });
    this.donation$.subscribe(item => {
      this.paymentType = item.type;
      if (!this.applyPaymentType()) {
        return;
      }

      this.amount = item.amount;
      if (properties.end_date) {
        this.recurringMethod = 'date';
        const end_date = new Date(properties.end_date);
        this.end = {
          year: end_date.getFullYear(),
          month: end_date.getMonth() + 1,
          day: end_date.getDate()
        };
        this.endDate = end_date;
        this.store.dispatch(
          new RecurringActions.SetEndDate({ date: end_date, count: undefined })
        );
        this.setClarification();
      } else if (properties.end_count) {
        this.recurringMethod = 'count';
        this.count = properties.end_count;
        this.store.dispatch(
          new RecurringActions.SetCount({
            count: this.count
          })
        );
        // calculate the end date if the user remains with the default count
        this.calcEndDate(this.frequencies[0].val, this.count);
      }
    });
    this.onChange(this.selected.val);
  }
  /**
   * PayPal and Google Pay cannot sustain a schedule, and Apple Pay only can
   * on devices that issue a merchant token (API 14+); otherwise force a
   * one-time gift and hide the picker. Returns whether recurring is offered.
   */
  private applyPaymentType(): boolean {
    const applePayRecurring =
      this.paymentType === 'apple_pay' && this.applePayRecurringSupported;
    if (
      this.paymentType === 'paypal' ||
      this.paymentType === 'google_pay' ||
      (this.paymentType === 'apple_pay' && !applePayRecurring)
    ) {
      this.onChange(Frequency.Once);
      this.isHidden = true;
      return false;
    }

    // Apple's sheet cannot express "1st and 15th", so it is withheld there
    this.frequencies = applePayRecurring
      ? this.allFrequencies.filter(
          item =>
            item.val === Frequency.Once || isApplePayRecurringFrequency(item.val)
        )
      : this.allFrequencies;
    if (!this.frequencies.find(item => item.val === this.selected.val)) {
      this.onChange(Frequency.Once);
    }
    this.isHidden = false;
    return true;
  }

  onChange($event) {
    GaClient.trackFieldAction('Frequency-Money');

    this.store.dispatch(new RecurringActions.SetFrequency($event));
    this.selected = this.frequencies.find(item => item.val === $event);
    this.setClarification();
  }
  setStartDate($event) {
    if ($event && $event.day) {
      this.startDate = new Date(
        $event.year,
        $event.month - 1,
        $event.day,
        0,
        0,
        0,
        0
      );
      // setting the end date minimum value
      this.minEndDate = $event;
      this.store.dispatch(new RecurringActions.SetStartDate(this.startDate));
      this.setClarification();
    } else {
      // no longer a valid date in the field
      this.store.dispatch(new RecurringActions.SetStartDate(undefined));
      // if the textbox is now empty, lets set start to undefined
      if (!$event) {
        this.clarification = undefined;
        this.minEndDate = this.minDate;
        this.startDate = undefined;
      }
    }
  }
  setEndDate($event) {
    if ($event && $event.day) {
      const newEndDate = new Date($event.year, $event.month - 1, $event.day);
      if (isBefore(newEndDate, threeMonthsOut)) {
        this.startLimit = this.dateAdapter.fromModel(newEndDate);
      } else {
        this.startLimit = this.dateAdapter.fromModel(threeMonthsOut);
      }
      this.endDate = newEndDate;
      this.store.dispatch(
        new RecurringActions.SetEndDate({ date: newEndDate, count: undefined })
      );
      this.setClarification();
    } else {
      // no longer a valid date in the field
      this.store.dispatch(
        new RecurringActions.SetEndDate({ date: undefined, count: undefined })
      );
      // if the textbox is now empty, lets set endDate to undefined to turn flip the
      // required state of the end date component
      if (!$event) {
        this.endDate = undefined;
        this.clarification = undefined;
        this.startLimit = this.dateAdapter.fromModel(threeMonthsOut);
      }
    }
  }
  calcEndDate(selected, count) {
    if (this.recurringMethod === 'count') {
      if (count < 0 || !count) {
        count = 0;
      }
      const start = new Date(this.startDate);
      let end;
      switch (selected) {
        case Frequency.Once:
          break;
        case Frequency.Weekly:
          end = addWeeks(start, count);
          break;
        case Frequency.BiWeekly:
          end = addWeeks(start, count * 2);
          break;
        case Frequency.TwiceMonthly:
          end = addWeeks(start, count * 2);
          break;
        case Frequency.Monthly:
          end = addMonths(start, count);
          break;
        case Frequency.Quarterly:
          end = addQuarters(start, count);
          break;
        case Frequency.SemiAnnually:
          end = addMonths(start, count * 6);
          break;
        case Frequency.Annually:
          end = addYears(start, count);
          break;
      }
      this.endDate = new Date(end);
      this.store.dispatch(
        new RecurringActions.SetEndDate({ date: this.endDate, count: count })
      );
      this.setClarification();
    }
  }
  private setClarification() {
    if (!this.showClarification()) {
      return;
    }
    let calculateMonths = false;
    let frequencyText = 'week';
    let dividend = 1;
    switch (this.selected.val) {
      case Frequency.Monthly:
        calculateMonths = true;
        frequencyText = 'month';
        break;
      case Frequency.Quarterly:
        calculateMonths = true;
        dividend = 3;
        frequencyText = 'quarter';
        break;
      case Frequency.SemiAnnually:
        dividend = 2;
        calculateMonths = true;
        frequencyText = 'semiannual';
        break;
      case Frequency.Annually:
        frequencyText = 'year';
        break;
    }
    let total = this.amount;
    let length = '';
    let endDateUpperLimit = this.endDate;
    if (this.recurringMethod == 'date') {
      // The end date will set the expiration date (at midnight), this means on the day itself
      // the recurring processor will not execute a payment, we need to take that into
      // account in the clarification text if the embed is using 'date' recurring type.
      // Chose to remove a day in calculation rather than pushing the selected date a day
      // because doing so modifies the users selected input if they donate and that might
      // raise concerns.
      endDateUpperLimit = addDays(endDateUpperLimit, -1);
    }
    switch (frequencyText) {
      case 'week':
        total =
          this.amount *
          this.adjustCounter(
            Math.abs(differenceInWeeks(this.startDate, endDateUpperLimit))
          );
        length = distanceInWordsStrict(this.startDate, endDateUpperLimit, {
          unit: 'd'
        });
        break;
      case 'month':
        total =
          this.amount *
          this.adjustCounter(
            Math.abs(differenceInMonths(this.startDate, endDateUpperLimit))
          );
        length = distanceInWordsStrict(this.startDate, endDateUpperLimit, {
          unit: 'M'
        });
        break;
      case 'quarter':
        total =
          this.amount *
          this.adjustCounter(
            Math.abs(differenceInQuarters(this.startDate, endDateUpperLimit))
          );
        length = distanceInWordsStrict(this.startDate, endDateUpperLimit, {
          unit: 'M'
        });
        break;
      case 'semiannual':
        total =
          this.amount *
          this.adjustCounter(
            Math.floor(
              Math.abs(
                differenceInMonths(this.startDate, endDateUpperLimit) / 6
              )
            )
          );
        length = distanceInWordsStrict(this.startDate, endDateUpperLimit, {
          unit: 'M'
        });
        break;
      case 'year':
        total =
          this.amount *
          this.adjustCounter(
            Math.abs(differenceInYears(this.startDate, endDateUpperLimit))
          );
        length = distanceInWordsStrict(this.startDate, endDateUpperLimit, {
          unit: 'Y'
        });
        break;
    }
    this.clarification = {
      length: length,
      amount: this.amount,
      total: total
    };
  }

  private showClarification() {
    return (
      this.selected &&
      this.endDate &&
      (this.recurringMethod !== 'count' || this.count > 0)
    );
  }

  private adjustCounter(amount) {
    // take the zero day donation into account when calculating
    // by end date, the [date range difference] functions do not.
    // The count method already extrapolates the 'end' date by +1 [date range]
    // to accomodate this. The midnight expiration is what prevents it from running
    // on that day.
    if (amount === 0) {
      return 1;
    }
    if (this.recurringMethod === 'count') {
      return amount;
    } else {
      return amount + 1;
    }
  }
}
